import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject,
} from '@angular/core';
import {
  BreakpointService,
  RoutingService,
  UrlSafePipe,
  WindowRef,
} from '@boels/helpers';
import {
  combineLatest,
  filter,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import {
  BoelsBaseSite,
  BoelsCategory,
  BoelsCategoryHierarchy,
} from '@boels/models';
import { Params, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { NgSelectModule } from '@ng-select/ng-select';
import {
  CatalogsFacade,
  CmsRouterLinkDirective,
  CultureFacade,
  ProductSearchFacade,
  SiteContextFacade,
} from '@boels/shared-state';
import { map } from 'rxjs/operators';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { FormsModule } from '@angular/forms';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { VerticalAlignerComponent } from '../vertical-aligner/vertical-aligner.component';

@Component({
  imports: [
    CommonModule,
    NgSelectModule,
    FormsModule,
    FontAwesomeModule,
    TranslocoModule,
    CmsRouterLinkDirective,
    VerticalAlignerComponent,
  ],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: ['productFilter'],
    },
    ProductSearchFacade,
  ],
  selector: 'boels-category-sibling-navigation',
  templateUrl: './category-sibling-navigation.component.html',
  styleUrls: ['./category-sibling-navigation.component.scss'],
  animations: [
    trigger('openClose', [
      state('open', style({})),
      state('closed', style({})),
      transition('open => closed', [
        style({ height: '*' }),
        animate('300ms', style({ height: '40px' })),
      ]),
      transition('closed => open', [
        style({ height: '40px' }),
        animate('300ms', style({ height: '*' })),
      ]),
    ]),
  ],
})
export class CategorySiblingNavigationComponent
  implements OnInit, AfterViewChecked
{
  #breakpointService = inject(BreakpointService);
  #routingService = inject(RoutingService);
  #router = inject(Router);
  #urlSafePipe = inject(UrlSafePipe);
  #catalogFacade = inject(CatalogsFacade);
  #siteContextFacade = inject(SiteContextFacade);
  #productSearchFacade = inject(ProductSearchFacade);
  #changeRef = inject(ChangeDetectorRef);
  #cultureFacade = inject(CultureFacade);
  #winRef = inject(WindowRef);

  protected readonly isTabletAndDown$ = toSignal(
    this.#breakpointService.mdAndBelow$,
  );
  protected readonly faChevronRightIcon = faChevronRight;
  protected readonly faChevronLeftIcon = faChevronLeft;
  protected showMoreCollapsed: boolean;
  protected allCategories!: BoelsCategory;
  protected categoriesList!: BoelsCategory;

  selectedCategory:
    | (BoelsCategoryHierarchy & { url?: string | undefined })
    | undefined;

  categories$ = combineLatest([
    this.#siteContextFacade.getActiveBaseSiteEntity(),
    this.#routingService
      .getRoutePropertyAsObservable('params')
      .pipe(filter(Boolean), takeUntilDestroyed()),
    this.#cultureFacade.getActiveCulture(),
  ])
    .pipe(
      filter(
        ([activeBaseSite, params, activeCulture]: [
          BoelsBaseSite,
          Params,
          string,
        ]) =>
          Boolean(activeBaseSite?.uid) &&
          Boolean(params['categoryId']) &&
          Boolean(activeCulture),
      ),
      switchMap(
        ([activeBaseSite, params, activeCulture]: [
          BoelsBaseSite,
          Params,
          string,
        ]) => {
          if (activeBaseSite.uid && activeCulture) {
            return this.#catalogFacade
              .getParentCategoryforId(
                activeBaseSite.uid,
                activeCulture,
                params['categoryId'],
              )
              .pipe(
                map((parentCategory) => {
                  const res = this.addUrlsToCategories(
                    params['categoryId'],
                    parentCategory,
                  );
                  this.allCategories = res as BoelsCategory;
                  this.createListToShow();
                  return res;
                }),
              );
          }
          return of(null);
        },
      ),
      filter(Boolean),
      tap(
        (categoryData) =>
          (this.selectedCategory = categoryData?.activeCategory),
      ),
      takeUntilDestroyed(),
    )
    .subscribe();

  public displayShowMore$: Observable<boolean>;

  ngOnInit(): void {
    const routParams = this.#routingService.getRouteProperty('params');
    const routeQueryParams =
      this.#routingService.getRouteProperty('queryParams');

    this.displayShowMore$ = this.#productSearchFacade
      .getProductsByCategoryId(
        routParams?.['categoryId'],
        0,
        routeQueryParams?.['sortCode'],
        routeQueryParams?.['query'],
      )
      .pipe(
        map((products) => {
          return products?.facets;
        }),
        map((data) => !!data?.length),
        tap((result) => {
          this.showMoreCollapsed = result;
          this.createListToShow();
        }),
      );
  }

  ngAfterViewChecked(): void {
    // This prevents the 'Expression changed after it has been checked' error.
    this.#changeRef.detectChanges();
  }

  addUrlsToCategories(
    categoryId: string,
    parentCategory?: BoelsCategoryHierarchy,
  ): {
    activeCategory: BoelsCategoryHierarchy & { url?: string };
    parentCategory: BoelsCategoryHierarchy & {
      subcategories?: (BoelsCategoryHierarchy & { url?: string })[];
      url?: string;
    };
  } | null {
    let activeCategory = parentCategory?.subcategories?.find(
      (subcategory) => subcategory.id === categoryId,
    );
    if (activeCategory) {
      const tempSubcategories = parentCategory?.subcategories?.map(
        (subcategory) => {
          return this.addUrlToCategory(subcategory);
        },
      );
      let tempParentCategory: BoelsCategoryHierarchy & {
        subcategories?: (BoelsCategoryHierarchy & { url?: string })[];
        url?: string;
      } = {
        ...parentCategory,
        subcategories: tempSubcategories,
      };
      tempParentCategory = this.addUrlToCategory(tempParentCategory);
      activeCategory = this.addUrlToCategory(activeCategory);
      return { activeCategory, parentCategory: tempParentCategory };
    }
    return null;
  }

  addUrlToCategory(
    category: BoelsCategoryHierarchy,
  ): BoelsCategoryHierarchy & { url?: string } {
    let url = '/';
    for (const breadcrumb of category.boelsBreadcrumbs || []) {
      url += this.#urlSafePipe.transform(breadcrumb.categoryName) + '/';
    }
    url += this.#urlSafePipe.transform(category.name) + '/c/' + category.id;
    return { ...category, url };
  }

  handleDropDownChange(
    newCategory: BoelsCategoryHierarchy & { url?: string },
  ): void {
    if (newCategory.url) {
      this.#cultureFacade
        .getActiveCulture()
        .pipe(filter(Boolean), take(1))
        .subscribe((culture) => {
          const url = [
            '/' + culture,
            ...(newCategory.url
              ?.split('/')
              .filter((value: string) => value.length > 0)
              .map((value: string) => this.#urlSafePipe.transform(value)) ||
              []),
          ];
          this.#router.navigate(url);
        });
    }
  }

  public toggleShowMore() {
    this.showMoreCollapsed = !this.showMoreCollapsed;

    this.createListToShow();
  }

  private createListToShow() {
    if (this.showMoreCollapsed && this.categoriesList) {
      this.#winRef.setTimeout(() => {
        if (this.showMoreCollapsed && this.categoriesList) {
          this.categoriesList = {
            ...this.categoriesList,
            parentCategory: {
              ...this.categoriesList?.parentCategory,
              subcategories:
                this.categoriesList?.parentCategory?.subcategories?.filter(
                  (item) => item?.id === this.allCategories?.activeCategory?.id,
                ) as BoelsCategoryHierarchy[],
            },
          };
        }
      }, 280);
    } else {
      this.categoriesList = this.allCategories;
    }
  }

  public trackById(index: number, subcategory: BoelsCategoryHierarchy): string {
    return subcategory.id as string;
  }
}
